// Canadian French (fr-CA) translations for the authenticated workspace shell:
// workspace switcher/cards, platform console, admin navigation and access-context
// header, Network Dashboard, and industry-preset display terminology.
//
// Keys are the canonical English strings used across the audited surfaces (the
// same key convention as the rest of the i18n dictionaries). This module is
// merged into the `fr` dictionary in I18nContext. English remains the default
// locale; any key missing here falls back to the English key at runtime and is
// reported by the localization parity check (scripts/check-fr-parity.mjs).

const frWorkspaceLocalization: Record<string, string> = {
  'Current page': 'Page actuelle',
  'Focus page search': 'Placer le curseur dans la recherche de la page',
  'Open page filters': 'Ouvrir les filtres de la page',
  'Refresh current list': 'Actualiser la liste actuelle',
  'Recently used': 'Utilisée récemment',
  'Recent commands could not be saved in this browser.': 'Les commandes récentes n’ont pas pu être enregistrées dans ce navigateur.',

  'Commands': 'Commandes',
  'Command palette': 'Palette de commandes',
  'Search commands': 'Rechercher des commandes',
  'Commands available': 'Commandes disponibles',
  'Use arrow keys to select, Enter to run, Escape to close.': 'Utilisez les flèches pour sélectionner, Entrée pour exécuter et Échap pour fermer.',
  'No matching commands. Try another search.': 'Aucune commande correspondante. Essayez une autre recherche.',
  'No commands available in this workspace.': 'Aucune commande disponible dans cet espace de travail.',
  'Unable to run this command. Please try again.': 'Impossible d’exécuter cette commande. Veuillez réessayer.',
  'Page hierarchy': 'Hiérarchie des pages',
  'Work / Post Work': 'Travail / Publier du travail',
  'Worksites / Punch Areas': 'Sites de travail / Zones de pointage',
  'Supervisors & Access': 'Superviseurs et accès',
  'Review Worked Time': 'Réviser les heures travaillées',
  'Prepare Payroll': 'Préparer la paie',
  'Post Work': 'Publier du travail',
  'Predictive Workforce Forecasting': 'Prévision prédictive des effectifs',
  'Forecast total site demand and translate it into stable FTE and temporary staffing plans.': 'Prévoyez la demande totale des sites et établissez des plans d’effectifs permanents en équivalents temps plein et de personnel temporaire.',
  'organization': 'organisation',
  'Predictive Forecasting': 'Prévisions prédictives',
  'Open My JobBank': 'Ouvrir ma banque d’emplois',
  'Job Bank': 'Banque d’emplois',
  'Browse active external opportunities discovered across configured market sources, separate from Nexus Jobs / Orders.': 'Parcourez les occasions externes actives repérées dans les sources de marché configurées, distinctes des emplois et commandes Nexus.',
  'Browse external opportunities': 'Parcourir les occasions externes',
  'Market Opportunities': 'Occasions du marché',
  'Manage the selected staffing agency’s private business-development pipeline for external demand.': 'Gérez le pipeline privé de développement des affaires de l’agence de placement sélectionnée pour la demande externe.',
  'Open agency pipeline': 'Ouvrir le pipeline de l’agence',
  'Job Sources': 'Sources d’emplois',
  'Configure, test, synchronize, and monitor external Market Demand providers.': 'Configurez, testez, synchronisez et surveillez les fournisseurs externes de demande du marché.',
  'Manage job sources': 'Gérer les sources d’emplois',
  'Market Employer Claims': 'Revendications d’employeurs du marché',
  'Review company ownership and representation claims for discovered employers.': 'Examinez les revendications de propriété et de représentation des employeurs repérés.',
  'Review claims': 'Examiner les revendications',
  'Start enrollment to protect your account with an authenticator app.': 'Commencez la configuration pour protéger votre compte avec une application d’authentification.',
  'This section is unavailable': 'Cette section est indisponible',
  'Complete the required authentication or retry to load current information.': 'Effectuez l’authentification requise ou réessayez pour charger les renseignements à jour.',
  'Unable to load this section. Please try again.': 'Impossible de charger cette section. Veuillez réessayer.',
  'Recovery codes were not returned. Sign in again before continuing.': 'Les codes de récupération n’ont pas été reçus. Reconnectez-vous avant de continuer.',

  'Company created. Share the supervisor invitation securely. The owner must accept before joining.': 'Entreprise créée. Transmettez l’invitation du superviseur de façon sécurisée. Le titulaire doit l’accepter avant de rejoindre l’espace.',
  'Open supervisor invitation': 'Ouvrir l’invitation du superviseur',
  'Site access still requires authorized location grants.': 'L’accès aux sites nécessite toujours des autorisations explicites.',
  'The supervisor signs in or sets up their own account through an invitation. Existing passwords and profiles remain under owner control.': 'Le superviseur se connecte ou crée son propre compte au moyen d’une invitation. Les mots de passe et profils existants restent sous le contrôle de leur titulaire.',

  'Schedule timezone': 'Fuseau horaire de l’horaire',
  'Timezone provenance unavailable': 'Provenance du fuseau horaire indisponible',
  'Legacy time — source timezone unknown': 'Heure historique — fuseau horaire source inconnu',
  'Device timezone fallback': 'Fuseau horaire de l’appareil utilisé par défaut',
  'Worker details': 'Détails du travailleur',
  'Shift details': 'Détails du quart',
  'Message worker': 'Écrire au travailleur',
  'Breadcrumbs': 'Trajet GPS',
  'Scheduled start': 'Début prévu',
  'Scheduled end': 'Fin prévue',
  'Conversation ready.': 'La conversation est prête.',
  'Open conversation': 'Ouvrir la conversation',
  'This worker is no longer active on this map.': 'Ce travailleur n’est plus actif sur cette carte.',
  'This location is unavailable or you no longer have access.': 'Cet emplacement est indisponible ou vous n’y avez plus accès.',
  'Live tracking is disabled by your platform administrator or plan.': 'Le suivi en direct est désactivé par l’administrateur de la plateforme ou votre forfait.',
  'Worker details are no longer available.': 'Les détails du travailleur ne sont plus disponibles.',
  'Shift details are no longer available.': 'Les détails du quart ne sont plus disponibles.',
  'Unable to open a conversation with this worker.': 'Impossible d’ouvrir une conversation avec ce travailleur.',
  // --- Workspace switcher & group labels ---------------------------------
  'Tenant operations': 'Opérations du locataire',
  'Sales / Revenue': 'Ventes et revenus',
  'Billing & plans': 'Facturation et forfaits',
  'Tools': 'Outils',
  'Current workspace': 'Espace de travail actuel',
  'Jump to the area you need.': 'Accédez à la section dont vous avez besoin.',
  'Workspaces': 'Espaces de travail',
  'Switch': 'Changer',
  'Close': 'Fermer',

  // --- Workspace card labels ---------------------------------------------
  'Admin workspace': 'Espace d’administration',
  'Worker workspace': 'Espace du travailleur',
  'Pricing & Products': 'Tarifs et produits',
  'Advanced Pricing Configuration': 'Configuration avancée des tarifs',
  'Worker mentorship': 'Mentorat du travailleur',
  'Shift imports': 'Importations de quarts',
  'Fence mapper': 'Cartographe de géorepères',
  'Super admin workspace': 'Espace du super administrateur',
  'Roles & Access': 'Rôles et accès',

  // --- Workspace card descriptions ---------------------------------------
  'System-wide administration: tenants, global users, platform billing, sales, and platform settings — no tenant selection required.':
    'Administration à l’échelle du système : locataires, utilisateurs globaux, facturation de la plateforme, ventes et paramètres de la plateforme — aucune sélection de locataire requise.',
  'Tenant operations: organization context, access, workers, jobs, timesheets, messaging, and settings.':
    'Opérations du locataire : contexte d’organisation, accès, travailleurs, emplois, feuilles de temps, messagerie et paramètres.',
  'Your platform learning home: continue learning, recommended skills, daily practice, streaks, and coaching — wherever you work.':
    'Votre espace d’apprentissage de la plateforme : poursuivez votre apprentissage, compétences recommandées, pratique quotidienne, séries et accompagnement — où que vous travailliez.',
  'Review assigned locations, rosters, breadcrumbs, messages, timesheets, and punch activity.':
    'Consultez les emplacements attribués, les horaires, les traces de déplacement, les messages, les feuilles de temps et l’activité de pointage.',
  'Manage today’s work, schedule, availability, timesheets, messages, profile, documents, skills, learning, and mentorship.':
    'Gérez le travail du jour, l’horaire, les disponibilités, les feuilles de temps, les messages, le profil, les documents, les compétences, l’apprentissage et le mentorat.',
  'Review this tenant’s subscription, usage, invoices, and plan.':
    'Consultez l’abonnement, l’utilisation, les factures et le forfait de ce locataire.',
  'Manage platform product mappings, pricing books, rules, regional prices, and tenant exceptions.':
    'Gérez les correspondances de produits de la plateforme, les catalogues de tarifs, les règles, les prix régionaux et les exceptions par locataire.',
  'Review platform revenue, client attribution, commissions, and sales representative portfolios.':
    'Consultez les revenus de la plateforme, l’attribution aux clients, les commissions et les portefeuilles des représentants commerciaux.',
  'Manage mentors, learners, sessions, pricing, and reviews.':
    'Gérez les mentors, les apprenants, les séances, la tarification et les évaluations.',
  'Manage goals, bookings, sessions, and mentor mode.':
    'Gérez les objectifs, les réservations, les séances et le mode mentor.',
  'Upload worker shift CSV files and monitor their processing status.':
    'Téléversez les fichiers CSV de quarts des travailleurs et suivez leur état de traitement.',
  'Launch the geofence mapper without leaving the admin console.':
    'Lancez le cartographe de géorepères sans quitter la console d’administration.',
  'Legacy super-admin workspace.': 'Espace hérité du super administrateur.',
  'Legacy roles workspace.': 'Espace hérité des rôles.',
  'Branding remains available inside Settings.': 'L’image de marque demeure accessible dans les Paramètres.',
  'Live Map is available inside Locations / Geofences.':
    'La carte en direct est accessible dans Emplacements / Géorepères.',

  // --- Workspace card CTAs -----------------------------------------------
  'Open Platform Console': 'Ouvrir la console de la plateforme',
  'Open admin': 'Ouvrir l’administration',
  'Open supervisor': 'Ouvrir le superviseur',
  'Open worker workspace': 'Ouvrir l’espace du travailleur',
  'Manage billing': 'Gérer la facturation',
  'Open advanced pricing': 'Ouvrir la tarification avancée',
  'Open sales': 'Ouvrir les ventes',
  'Open Mentor Admin': 'Ouvrir l’administration du mentorat',
  'Open mentorship': 'Ouvrir le mentorat',
  'Manage shifts': 'Gérer les quarts',
  'Open workspace': 'Ouvrir l’espace de travail',

  // --- Admin navigation group labels -------------------------------------
  'Home': 'Accueil',
  'People': 'Personnel',
  'Work': 'Travail',
  'Network': 'Réseau',
  'Approvals': 'Approbations',
  'Communication and Insights': 'Communications et analyses',

  // --- Admin navigation item labels --------------------------------------
  'Network Dashboard': 'Tableau de bord du réseau',
  'Staff / Workers': 'Personnel / Travailleurs',
  'Users & Access': 'Utilisateurs et accès',
  'Schedule': 'Horaire',
  'Project Work Log': 'Journal de travail du projet',
  'Locations / Geofences': 'Emplacements / Géorepères',
  'Network Relationships': 'Relations du réseau',
  'Timesheet Approvals': 'Approbations des feuilles de temps',
  'Decision Reviews': 'Révisions des décisions',
  'Messages': 'Messages',
  'Reports': 'Rapports',
  'Org Structure': 'Structure organisationnelle',
  'Skills': 'Compétences',
  'Settings': 'Paramètres',
  'Tenants': 'Locataires',
  'Organizations': 'Organisations',
  'Labour Rule Profiles': 'Profils de règles du travail',
  'Configure and inspect effective jurisdiction profiles and automatic advisories.':
    'Configurez et consultez les profils de juridiction en vigueur et les avis automatiques.',
  'Location Privacy': 'Confidentialité de la localisation',
  'Control purpose-bound collection, retention, and evidence holds.':
    'Contrôlez la collecte limitée à une finalité, la conservation et les mises en attente de preuves.',
  'OpenAPI Contract': 'Contrat OpenAPI',
  'Inspect the canonical versioned integration contract.':
    'Consultez le contrat d’intégration canonique et versionné.',
  'Webhook Integrations': 'Intégrations par webhook',
  'Inspect signed webhook subscriptions and delivery state.':
    'Consultez les abonnements webhook signés et leur état de livraison.',
  'Review worker appeals for material access decisions within your authorized scope.':
    'Révisez les appels des travailleurs concernant les décisions importantes d’accès dans votre portée autorisée.',

  // --- Access-context header ---------------------------------------------
  'Scope / role': 'Portée / rôle',
  'System Super Admin': 'Super administrateur système',
  'Platform admin': 'Administrateur de la plateforme',
  'Platform Admin': 'Administrateur de la plateforme',
  'System role': 'Rôle système',
  'System-level roles and platform permission assignments.':
    'Rôles au niveau du système et attributions de permissions de la plateforme.',
  'Tenant Admin': 'Administrateur du locataire',
  'Tenant context': 'Contexte du locataire',
  'Organization context is fixed for your current access.':
    'Le contexte d’organisation est fixe pour votre accès actuel.',
  'Organization context unavailable': 'Contexte d’organisation non disponible',
  'Organization switching is read-only for your current access.':
    'Le changement d’organisation est en lecture seule pour votre accès actuel.',
  'Scope selectors are read-only unless your role can switch tenant, organization, OU, or location context.':
    'Les sélecteurs de portée sont en lecture seule, sauf si votre rôle peut changer le contexte de locataire, d’organisation, d’unité organisationnelle ou d’emplacement.',
  'Selecting a tenant does not make you a tenant member; backend permissions still decide each action.':
    'Sélectionner un locataire ne fait pas de vous un membre de ce locataire; les permissions du serveur déterminent toujours chaque action.',
  'No selectable organization is available.': 'Aucune organisation sélectionnable n’est disponible.',
  'No organization selected': 'Aucune organisation sélectionnée',
  'No tenant is available': 'Aucun locataire n’est disponible',
  'No tenant membership (global identity)': 'Aucune adhésion à un locataire (identité globale)',
  'Tenant memberships': 'Adhésions aux locataires',
  'No tenant membership': 'Aucune adhésion à un locataire',
  'Switch location context': 'Changer le contexte d’emplacement',
  'Switch organization context': 'Changer le contexte d’organisation',
  'Switch organization unit context': 'Changer le contexte d’unité organisationnelle',
  'Switch tenant context': 'Changer le contexte de locataire',
  'Selected location': 'Emplacement sélectionné',
  'Select an organization': 'Sélectionnez une organisation',
  'role unavailable': 'rôle non disponible',
  'Unknown user': 'Utilisateur inconnu',
  'platform user': 'utilisateur de la plateforme',
  'member': 'membre',
  'Platform scope — all tenants': 'Portée de la plateforme — tous les locataires',
  'Platform scope — select a tenant…': 'Portée de la plateforme — sélectionnez un locataire…',
  'Tenant name': 'Nom du locataire',
  'Tenant slug (optional)': 'Identifiant du locataire (optionnel)',
  'Unable to create tenant.': 'Impossible de créer le locataire.',
  'Refresh tenants': 'Actualiser les locataires',
  'Refreshing…': 'Actualisation…',
  'Loading tenants…': 'Chargement des locataires…',
  'Loading global users…': 'Chargement des utilisateurs globaux…',
  'Searching…': 'Recherche…',
  'Search by email, phone, or name': 'Rechercher par courriel, téléphone ou nom',
  'Search global users': 'Rechercher des utilisateurs globaux',
  'No users match the current filters.': 'Aucun utilisateur ne correspond aux filtres actuels.',

  // --- Access / permission gating states ---------------------------------
  'Loading your network access, organizations, and locations...':
    'Chargement de votre accès au réseau, de vos organisations et de vos emplacements…',
  'We could not load your workspace permissions yet. You can retry, and backend permissions still protect every action.':
    'Nous n’avons pas encore pu charger vos permissions d’espace de travail. Vous pouvez réessayer; les permissions du serveur protègent toujours chaque action.',
  'Refresh the workspace. If this keeps happening, ask an owner to verify the access context response.':
    'Actualisez l’espace de travail. Si le problème persiste, demandez à un propriétaire de vérifier la réponse du contexte d’accès.',
  'You are signed in, but this account does not have Admin Workspace access.':
    'Vous êtes connecté, mais ce compte n’a pas accès à l’espace d’administration.',
  'You are signed in, but this account does not have platform administration access.':
    'Vous êtes connecté, mais ce compte n’a pas accès à l’administration de la plateforme.',
  'Your account is signed in, but there is no organization in the current tenant context yet.':
    'Votre compte est connecté, mais aucune organisation n’existe encore dans le contexte de locataire actuel.',
  'Permission-limited route': 'Route à permissions restreintes',
  'No settings, branding, organization, billing, or tenant-wide configuration controls are rendered for this route.':
    'Aucun contrôle de paramètres, d’image de marque, d’organisation, de facturation ou de configuration à l’échelle du locataire n’est affiché pour cette route.',
  'The requested Admin destination is not available in your current tenant, organization, or location scope.':
    'La destination d’administration demandée n’est pas disponible dans votre portée actuelle de locataire, d’organisation ou d’emplacement.',
  'You are in platform scope. Select a tenant to open tenant-owned Admin destinations, or return to Platform Console.':
    'Vous êtes en portée de plateforme. Sélectionnez un locataire pour ouvrir les destinations d’administration propres au locataire, ou revenez à la console de la plateforme.',

  // --- Platform Console ---------------------------------------------------
  'Platform areas': 'Sections de la plateforme',
  'Platform Settings': 'Paramètres de la plateforme',
  'Platform Roles & Permissions': 'Rôles et permissions de la plateforme',
  'Tenant management': 'Gestion des locataires',
  'System Diagnostics': 'Diagnostics du système',
  'Sales / CRM': 'Ventes / GRC',
  'WhatsApp Integration': 'Intégration WhatsApp',
  'White-label Branding': 'Image de marque en marque blanche',
  'Security': 'Sécurité',
  'Messaging': 'Messagerie',
  'System-wide administration for this platform installation. This is not tenant administration: select a tenant only when you need to manage tenant-owned data.':
    'Administration à l’échelle du système pour cette installation de la plateforme. Il ne s’agit pas de l’administration d’un locataire : sélectionnez un locataire uniquement lorsque vous devez gérer des données propres à un locataire.',
  'Create, inspect, and enter tenant workspaces across the whole platform.':
    'Créez, inspectez et accédez aux espaces de travail des locataires sur l’ensemble de la plateforme.',
  'Cross-tenant organization directory and relationship tools.':
    'Répertoire d’organisations inter-locataires et outils de relations.',
  'Installation-wide defaults and platform configuration.':
    'Valeurs par défaut à l’échelle de l’installation et configuration de la plateforme.',
  'Runtime health, storage checks, and platform observability.':
    'État d’exécution, vérifications du stockage et observabilité de la plateforme.',
  'Platform billing overview, subscriptions, and revenue.':
    'Aperçu de la facturation de la plateforme, abonnements et revenus.',
  'Platform revenue attribution, commissions, and sales representative portfolios.':
    'Attribution des revenus de la plateforme, commissions et portefeuilles des représentants commerciaux.',
  'Send operational reminders and worker notifications through WhatsApp.':
    'Envoyez des rappels opérationnels et des notifications aux travailleurs par WhatsApp.',
  'Default branding applied before tenants customize their own.':
    'Image de marque par défaut appliquée avant que les locataires personnalisent la leur.',
  'Inspect global identities, tenant memberships, and system roles without selecting a tenant.':
    'Inspectez les identités globales, les adhésions aux locataires et les rôles système sans sélectionner de locataire.',
  'Read-only platform directory of global identities. Tenant is a filter here, never a required gate; tenant-scoped user changes happen inside a selected tenant.':
    'Répertoire de plateforme en lecture seule des identités globales. Le locataire n’est ici qu’un filtre, jamais une condition requise; les modifications d’utilisateurs propres à un locataire se font dans un locataire sélectionné.',
  'The global user directory requires platform tenant-management access.':
    'Le répertoire des utilisateurs globaux nécessite un accès à la gestion des locataires de la plateforme.',
  'The tenant directory requires platform tenant-management access.':
    'Le répertoire des locataires nécessite un accès à la gestion des locataires de la plateforme.',
  'Unable to load the global user directory.': 'Impossible de charger le répertoire des utilisateurs globaux.',
  'Unable to load the tenant directory.': 'Impossible de charger le répertoire des locataires.',
  'View global users': 'Voir les utilisateurs globaux',
  'View tenant directory': 'Voir le répertoire des locataires',
  'Open platform billing': 'Ouvrir la facturation de la plateforme',
  'Open sales workspace': 'Ouvrir l’espace des ventes',
  'Open Tenant Mentor Admin': 'Ouvrir l’administration du mentorat du locataire',
  'Global AI mentor templates, starter tracks, and platform learning defaults. Tenant mentorship operations stay in the tenant Mentor Admin workspace.':
    'Modèles globaux de mentors IA, parcours de démarrage et valeurs par défaut d’apprentissage de la plateforme. Les opérations de mentorat des locataires demeurent dans l’espace d’administration du mentorat du locataire.',
  'Global catalog console is not connected yet. Tenant-specific mentorship requires selecting a tenant.':
    'La console du catalogue global n’est pas encore connectée. Le mentorat propre à un locataire nécessite la sélection d’un locataire.',
  'Not connected yet — available after the platform endpoint is connected.':
    'Pas encore connecté — disponible une fois le point de terminaison de la plateforme connecté.',
  'NexusWorkforce360 Workforce Network OS': 'Système d’exploitation du réseau de main-d’œuvre NexusWorkforce360',

  // --- Network Dashboard / operational overview --------------------------
  'Network operations overview': 'Aperçu des opérations du réseau',
  'Operational overview': 'Aperçu opérationnel',
  'Compact metrics backed by the current access context and scoped backend summaries.':
    'Indicateurs compacts appuyés par le contexte d’accès actuel et les résumés du serveur selon la portée.',
  'High-level demand and approval signals only. Open the destination screen for detailed scheduling, staffing, and timesheets.':
    'Signaux de haut niveau sur la demande et les approbations seulement. Ouvrez l’écran de destination pour l’horaire, la dotation et les feuilles de temps détaillés.',
  'Only real approval or relationship states from your authorized context are shown here.':
    'Seuls les états réels d’approbation ou de relation de votre contexte autorisé sont affichés ici.',
  'No pending approvals or relationship setup issues are visible in this scope.':
    'Aucune approbation en attente ni problème de configuration de relation n’est visible dans cette portée.',
  'No immediate action': 'Aucune action immédiate',
  'Network composition': 'Composition du réseau',
  'Organization mix': 'Répartition des organisations',
  'Work and coverage': 'Travail et couverture',
  'Work requiring coverage': 'Travail nécessitant une couverture',
  'Setup checklist': 'Liste de vérification de la configuration',
  'Setup status is not available from the access context yet.':
    'L’état de la configuration n’est pas encore disponible depuis le contexte d’accès.',
  'Own staff': 'Personnel propre',
  'Own workers': 'Travailleurs propres',
  'Own staff and network-visible workforce stay separated so partner supply never reads as Staff.':
    'Le personnel propre et la main-d’œuvre visible sur le réseau restent distincts, de sorte que l’offre des partenaires n’est jamais comptée comme du personnel.',
  'Partner / network workforce': 'Main-d’œuvre partenaire / du réseau',
  'Network-visible workers': 'Travailleurs visibles sur le réseau',
  'Network-visible worker relationship rows; not counted as organization-owned staff.':
    'Lignes de relation de travailleurs visibles sur le réseau; non comptabilisées comme personnel appartenant à l’organisation.',
  'Pending applications': 'Candidatures en attente',
  'Pending assignments': 'Affectations en attente',
  'Assignments awaiting worker acceptance or company approval.':
    'Affectations en attente d’acceptation par le travailleur ou d’approbation par l’entreprise.',
  'Accessible jobs in posted, open, or partially filled status.':
    'Emplois accessibles ayant le statut publié, ouvert ou partiellement comblé.',
  'Accessible jobs with posted, open, or partially filled status.':
    'Emplois accessibles ayant le statut publié, ouvert ou partiellement comblé.',
  'Accessible submitted or shortlisted applications plus assignments pending worker or company approval.':
    'Candidatures soumises ou présélectionnées accessibles, plus les affectations en attente d’approbation du travailleur ou de l’entreprise.',
  'Submitted or shortlisted applications on accessible jobs.':
    'Candidatures soumises ou présélectionnées pour des emplois accessibles.',
  'Active company-agency relationships in the selected organization scope, excluding managed legacy profiles.':
    'Relations actives entreprise-agence dans la portée d’organisation sélectionnée, à l’exclusion des profils hérités gérés.',
  'Active worker relationship rows visible in this tenant or scoped user context. This is not the Staff count.':
    'Lignes de relation de travailleurs actifs visibles dans ce locataire ou ce contexte d’utilisateur; il ne s’agit pas du nombre de membres du personnel.',
  'Active worker tenant membership plus active ownership relationship.':
    'Adhésion active du travailleur au locataire et relation de propriété active.',
  'Limited-profile free agents visible through the sharing model.':
    'Agents libres à profil limité, visibles grâce au modèle de partage.',
  'Visible organizations returned by your access context.':
    'Organisations visibles retournées par votre contexte d’accès.',
  'Locations returned in your access context; supervisors only see authorized location scope.':
    'Emplacements retournés dans votre contexte d’accès; les superviseurs ne voient que la portée d’emplacements autorisée.',
  'Server did not return the organization-owned staff count for this org.':
    'Le serveur n’a pas retourné le nombre de membres du personnel appartenant à cette organisation.',
  'Select a tenant organization before viewing organization-owned staff.':
    'Sélectionnez une organisation locataire avant d’afficher le personnel appartenant à l’organisation.',
  'A short operational list of partner relationships. Detailed policy, grants, and lifecycle controls stay in Network Relationships.':
    'Une courte liste opérationnelle des relations avec les partenaires. Les politiques détaillées, les octrois et les contrôles de cycle de vie demeurent dans Relations du réseau.',

  // --- Dashboard action buttons ------------------------------------------
  'Open Schedule': 'Ouvrir l’horaire',
  'Open Staff': 'Ouvrir le personnel',
  'Open Relationships': 'Ouvrir les relations',
  'Open Live Map': 'Ouvrir la carte en direct',
  'Open Locations': 'Ouvrir les emplacements',
  'Open jobs': 'Ouvrir les emplois',
  'Open branding': 'Ouvrir l’image de marque',
  'Open setup': 'Ouvrir la configuration',
  'Open tenant admin': 'Ouvrir l’administration du locataire',
  'Open relationship': 'Ouvrir la relation',
  'Export approved timesheets to payroll-ready files.':
    'Exportez les feuilles de temps approuvées vers des fichiers prêts pour la paie.',
  'Communicate with workers and operational teams in the current scope.':
    'Communiquez avec les travailleurs et les équipes opérationnelles dans la portée actuelle.',
  'Review scoped workforce, attendance, and approval readiness reporting.':
    'Consultez les rapports sur la main-d’œuvre, la présence et l’état d’avancement des approbations selon la portée.',
  'Manage projects, tasks, daily project time, and weekly project timesheets.':
    'Gérez les projets, les tâches, le temps de projet quotidien et les feuilles de temps de projet hebdomadaires.',
  'Canonical skills shared by jobs, worker profiles, and learning.':
    'Compétences canoniques partagées par les emplois, les profils de travailleurs et l’apprentissage.',
  'Customize organization identity, colors, logos, banners, and support details.':
    'Personnalisez l’identité de l’organisation, les couleurs, les logos, les bannières et les coordonnées de soutien.',
  'Organization brand colors': 'Couleurs de marque de l’organisation',
  'Review or edit company profile, locations, geofences, supervisors, workers, and launch readiness.':
    'Consultez ou modifiez le profil de l’entreprise, les emplacements, les géorepères, les superviseurs, les travailleurs et l’état de préparation au lancement.',
  'Add a display name, logo URL, brand colors, and support contact details for':
    'Ajoutez un nom d’affichage, une URL de logo, des couleurs de marque et des coordonnées de soutien pour',

  // --- Tenant setup states -----------------------------------------------
  'Tenant operations need a selected tenant': 'Les opérations du locataire nécessitent un locataire sélectionné',
  'A tenant is currently selected for tenant-owned tools.':
    'Un locataire est actuellement sélectionné pour les outils propres au locataire.',
  'Select a tenant to manage tenant-owned data — workers, jobs, locations, timesheets, tenant billing, tenant branding, and tenant mentorship.':
    'Sélectionnez un locataire pour gérer les données propres au locataire — travailleurs, emplois, emplacements, feuilles de temps, facturation du locataire, image de marque du locataire et mentorat du locataire.',
  'Select a tenant to manage tenant-owned data.':
    'Sélectionnez un locataire pour gérer les données propres au locataire.',
  'Create an organization to finish setup': 'Créez une organisation pour terminer la configuration',
  'Create the first tenant workspace to start onboarding organizations.':
    'Créez le premier espace de travail de locataire pour commencer à intégrer des organisations.',
  'No tenants exist yet. Create the first tenant workspace to start onboarding organizations.':
    'Aucun locataire n’existe encore. Créez le premier espace de travail de locataire pour commencer à intégrer des organisations.',
  'No tenants exist yet. Create the first tenant from the Admin Workspace tenant setup card.':
    'Aucun locataire n’existe encore. Créez le premier locataire à partir de la carte de configuration de locataire de l’espace d’administration.',
  'Shared Agency SaaS controls for organizations, access, workers, jobs, and operations.':
    'Contrôles SaaS d’agence partagés pour les organisations, l’accès, les travailleurs, les emplois et les opérations.',
  'setup steps are complete.': 'étapes de configuration sont terminées.',
  'Todo': 'À faire',

  // --- Access-context fragments (counts, nouns) --------------------------
  'agencies': 'agences',
  'companies': 'entreprises',
  'destinations': 'destinations',
  'locations': 'emplacements',
  'visible locations': 'emplacements visibles',
  'direct active workers in this organization. Partner-visible, shared, and assigned workers are excluded.':
    'travailleurs actifs directs dans cette organisation. Les travailleurs visibles par les partenaires, partagés et affectés sont exclus.',
  'pending worker or company assignment approvals': 'approbations d’affectation de travailleur ou d’entreprise en attente',
  'submitted or shortlisted applications': 'candidatures soumises ou présélectionnées',

  // --- Organization type humanized labels (via formatOrgType + t()) ------
  'Agency': 'Agence',
  'Company': 'Entreprise',
  'Platform Owner': 'Propriétaire de la plateforme',
  'Organization': 'Organisation',

  // --- Role display labels (enumDisplayLabels + t()) ---------------------
  'Company Admin': 'Administrateur de l’entreprise',
  'Agency Admin': 'Administrateur de l’agence',
  'Corporate User': 'Utilisateur d’entreprise',
  'Supervisor': 'Superviseur',
  'Worker': 'Travailleur',
  'No role grants returned': 'Aucune attribution de rôle retournée',
  '{visible} +{count} more': '{visible} +{count} de plus',

  // --- Industry preset display names -------------------------------------
  'General Workforce Operations': 'Opérations générales de main-d’œuvre',
  'Staffing Agency': 'Agence de placement',
  'Security Company': 'Entreprise de sécurité',
  'Healthcare Provider': 'Fournisseur de soins de santé',
  'Home Care Agency': 'Agence de soins à domicile',
  'Hospitality / Events': 'Hôtellerie / Événements',
  'Construction / Field Services': 'Construction / Services sur le terrain',
  'Property Services': 'Services immobiliers',

  // --- Industry preset terminology (via useIndustryTerminology + t()) ----
  'Candidates / Workers': 'Candidats / Travailleurs',
  'Client Orders': 'Commandes clients',
  'Client Sites': 'Sites clients',
  'Guard': 'Agent de sécurité',
  'Guards': 'Agents de sécurité',
  'Contracts / Posts': 'Contrats / Postes',
  'Site': 'Site',
  'Dispatch Supervisor': 'Superviseur de répartition',
  'Nurses / Staff': 'Infirmières / Personnel',
  'Staffing Requests': 'Demandes de dotation',
  'Facilities / Units': 'Établissements / Unités',
  'Credentials': 'Accréditations',
  'Caregiver': 'Aidant',
  'Caregivers': 'Aidants',
  'Visits / Care Assignments': 'Visites / Affectations de soins',
  'Client Home': 'Domicile du client',
  'Client Homes': 'Domiciles des clients',
  'Events / Service Orders': 'Événements / Ordres de service',
  'Venue': 'Lieu',
  'Venues': 'Lieux',
  'Crews / Workers': 'Équipes / Travailleurs',
  'Projects / Work Orders': 'Projets / Bons de travail',
  'Job Site': 'Chantier',
  'Job Sites': 'Chantiers',
  'Safety Docs': 'Documents de sécurité',
  'Field Staff': 'Personnel de terrain',
  'Service Orders': 'Ordres de service',
  'Properties / Sites': 'Propriétés / Sites',

  // --- Industry preset setup steps ---------------------------------------
  'Confirm organization profile': 'Confirmer le profil de l’organisation',
  'Add locations': 'Ajouter des emplacements',
  'Review approvals and payroll exports': 'Réviser les approbations et les exportations de paie',
  'Create first job/order': 'Créer le premier emploi/la première commande',
  'Create agency profile': 'Créer le profil de l’agence',
  'Add client sites': 'Ajouter des sites clients',
  'Create first client order': 'Créer la première commande client',
  'Review approval/export workflow': 'Réviser le flux d’approbation/d’exportation',
  'Add security sites': 'Ajouter des sites de sécurité',
  'Invite guards': 'Inviter des agents de sécurité',
  'Create posts or contracts': 'Créer des postes ou des contrats',
  'Assign supervisors': 'Attribuer des superviseurs',
  'Review incident and attendance proof workflow': 'Réviser le flux de preuve d’incident et de présence',
  'Add facilities or units': 'Ajouter des établissements ou des unités',
  'Track credentials': 'Suivre les accréditations',
  'Create staffing requests': 'Créer des demandes de dotation',
  'Review approval workflow': 'Réviser le flux d’approbation',
  'Add client homes': 'Ajouter des domiciles de clients',
  'Invite caregivers': 'Inviter des aidants',
  'Create care assignments': 'Créer des affectations de soins',
  'Review visit approval workflow': 'Réviser le flux d’approbation des visites',
  'Add venues': 'Ajouter des lieux',
  'Invite staff': 'Inviter du personnel',
  'Create events or service orders': 'Créer des événements ou des ordres de service',
  'Review open shifts': 'Réviser les quarts ouverts',
  'Configure approvals': 'Configurer les approbations',
  'Add job sites': 'Ajouter des chantiers',
  'Invite crews/workers': 'Inviter des équipes/travailleurs',
  'Track safety docs': 'Suivre les documents de sécurité',
  'Create projects or work orders': 'Créer des projets ou des bons de travail',
  'Review timesheet approvals': 'Réviser les approbations des feuilles de temps',
  'Add properties/sites': 'Ajouter des propriétés/sites',
  'Invite field staff': 'Inviter du personnel de terrain',
  'Create service orders': 'Créer des ordres de service',
  'Review proof-of-service workflow': 'Réviser le flux de preuve de service',
  'Configure billing/payroll exports': 'Configurer les exportations de facturation/paie',
}

export default frWorkspaceLocalization
